import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { Alert, Box, DuoColorIcon, Icon, Text } from '@zillow/constellation-10';
import { IconBuildingFilled, IconDoorOpenFilled } from '@zillow/constellation-icons';
import { DatarouterError } from '@zg-rentals/web-base';
import { useMultiplexBuildingSessionId, useMultiplexUnitPublished } from '../../../../hooks/queries/multiplex';
import stringHelper from '../../../../utils/stringHelper';

import { CELEBRATE_ILLUSTRATION_URL } from '../../../../constants/DigitalLibraryConstants';
import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import { getCreateListingRoute } from '../../../../constants/urls';
import { EXIT_CTA_LABEL } from '../../../../constants/WizardConstants';
import { STEP_KEYS } from '../../constants/stepKeys';

import MultiplexListingNavCard from './components/MultiplexListingNavCard';

import SpinnerContainer from '../../../SpinnerContainer';
import PageWizard from '../../../Wizard/PageWizard';
import { useWizardNavigation } from '../../WizardNavigationProvider';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const MultiplexListingPublished = ({
  onGoToNextStep: _wizardNextOmitted,
  onGoToPrevStep: _wizardPrevOmitted,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const { goToStep } = useWizardNavigation();
  const listingAlias = router.query.listingAlias as string;

  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  const { data, error, isFetching } = useMultiplexUnitPublished();

  const hasError = Boolean(error);
  const multiplexUnitPublishedErrorMessage = hasError
    ? DatarouterError.getDisplayMessage(error, DEFAULT_ERROR_MESSAGE)
    : null;

  const unpublishedCount = data?.unpublishedCount ?? null;
  const hasUnpublishedUnits = unpublishedCount != null && unpublishedCount > 0;
  const publishedUnitNumber = data?.publishedUnitNumber ?? '';
  const nextUnitNumber = data?.nextUnitNumber ?? '';
  const nextUnitAlias = data?.nextUnitAlias ?? '';
  const sessionIdForNavigation = data?.multiplexBuildingSessionId ?? multiplexBuildingSessionId ?? '';

  const showNextUnitCard =
    Boolean(data) && hasUnpublishedUnits && Boolean(nextUnitAlias.trim()) && Boolean(nextUnitNumber.trim());
  const showSpinner = Boolean(listingAlias) && isFetching && !data;
  const showSuccessContent = Boolean(data) && !hasError;
  const canNavigateBack = Boolean(listingAlias) && Boolean(sessionIdForNavigation.trim());
  const [isNavigationPending, setIsNavigationPending] = useState(false);

  useEffect(() => {
    setIsNavigationPending(false);
  }, [listingAlias, sessionIdForNavigation, nextUnitAlias]);

  const navigateToCreateListing = async (propertyListingAlias: string, stepKey: string) => {
    setIsNavigationPending(true);

    try {
      await router.push(
        getCreateListingRoute({
          listingAlias: propertyListingAlias,
          multiplexBuildingSessionId: sessionIdForNavigation,
        }),
      );

      goToStep(stepKey, { listingAlias: propertyListingAlias });
    } finally {
      setIsNavigationPending(false);
    }
  };

  const handleListNextUnitClick = () => {
    if (isNavigationPending || !sessionIdForNavigation || !nextUnitAlias.trim()) return;

    void navigateToCreateListing(nextUnitAlias, STEP_KEYS.REVIEW);
  };

  const handleBackToPropertyClick = () => {
    if (isNavigationPending || !listingAlias || !sessionIdForNavigation) return;

    goToStep(STEP_KEYS.MULTIPLEX_OVERVIEW);
  };

  const unpublishedSetupSentence = hasUnpublishedUnits
    ? `  You have ${stringHelper.pluralize(unpublishedCount ?? 0, 'more unit to setup', 'more units to setup')}.`
    : '';

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      onExitTriggered={onExitWizard}
      hideFooter={true}
      displayPremiumTopOfFunnelHeader={false}
    >
      <Box
        css={{
          margin: '0 auto',
          maxWidth: '500px',
          width: '100%',
        }}
        data-testid="MultiplexListingPublished"
      >
        {hasError && (
          <Alert data-testid="MultiplexListingPublishedError" tone="critical" css={{ marginBlockEnd: '300' }}>
            {multiplexUnitPublishedErrorMessage}
          </Alert>
        )}

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '400',
            justifyContent: 'center',
          }}
        >
          {showSpinner && (
            <Box css={{ position: 'relative', bottom: '25px' }}>
              <SpinnerContainer size="lg" height="100px" />
            </Box>
          )}

          {showSuccessContent && (
            <Box
              data-testid="HeaderContainer"
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '400',
                justifyContent: 'flex-start',
                textAlign: 'left',
              }}
            >
              <Image src={CELEBRATE_ILLUSTRATION_URL} alt="Celebration" width={120} height={120} />

              <Text textStyle="heading-sm">Listing published</Text>

              <Text textStyle="body">
                {publishedUnitNumber &&
                  `Unit ${publishedUnitNumber} will be live on the Zillow Rental Network soon.${unpublishedSetupSentence}`}
              </Text>
            </Box>
          )}

          {(showSuccessContent || (hasError && canNavigateBack)) && (
            <Box
              data-testid="UnitListContainer"
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                gap: '200',
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
                {showSuccessContent && showNextUnitCard && (
                  <MultiplexListingNavCard
                    data-testid="NextUnitCard"
                    isNavigationPending={isNavigationPending}
                    title={`List unit ${nextUnitNumber} next`}
                    subtitle="Continue setting up your next unit’s listing"
                    leading={
                      <DuoColorIcon tone="info">
                        <Icon size="lg" render={<IconDoorOpenFilled />} />
                      </DuoColorIcon>
                    }
                    onActivate={handleListNextUnitClick}
                  />
                )}

                {canNavigateBack && (
                  <MultiplexListingNavCard
                    data-testid="BackToPropertyCard"
                    isNavigationPending={isNavigationPending}
                    title="Back to property"
                    subtitle="View all units and manage your property"
                    leading={
                      <DuoColorIcon tone="info">
                        <Icon size="lg" render={<IconBuildingFilled />} />
                      </DuoColorIcon>
                    }
                    onActivate={handleBackToPropertyClick}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </PageWizard>
  );
};

export default MultiplexListingPublished;
